import { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { Loader, ArrowRight } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { getProjectsByCustomerId } from '../api/Customer.api.js';
import { getCamaraFirstImage } from '../../camaraManager/api/camaraManager.api';
import ProjectLastFrameImage from './ProjectLastFrameImage.js';

const getProjectCameraUid = (project: any) => (
  project?.camaraUID || project?.cameraUID || project?.camaraUid || ''
);

interface ArchiveCamerasProps {
  customerId?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

function ArchiveCameras({ customerId: propCustomerId, isOpen = true, onClose }: ArchiveCamerasProps) {
  const { customerId: routeCustomerId } = useParams();
  const customerId = propCustomerId || routeCustomerId;
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [imageOrientations, setImageOrientations] = useState<Record<string, string>>({});
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [ftps3ImageUrls, setFtps3ImageUrls] = useState<Record<string, string>>({});
  const [imageRetryCounts, setImageRetryCounts] = useState<Record<string, number>>({});
  const [imageErrorStates, setImageErrorStates] = useState<Record<string, boolean>>({});
  const MAX_IMAGE_RETRIES = 2;

  const fetchSingleFtps3ImageUrl = async (projectId: string, feedID: string) => {
    try {
      const data = await getCamaraFirstImage(feedID);

      if (data?.url) {
        setFtps3ImageUrls((prev) => ({
          ...prev,
          [projectId]: data.url,
        }));

        setImageLoadingStates((prev) => ({
          ...prev,
          [projectId]: true,
        }));
        return true;
      }
      return false;
    } catch (error: any) {
      // 404: la cámara no existe o todavía no tiene firstImage generada en el back.
      // 409: el status en Firebase todavía no está sincronizado como "Terminado".
      // Ambos son casos esperables, no errores reales: solo no hay foto para mostrar.
      const status = error?.response?.status;
      if (status !== 404 && status !== 409) {
        console.error(`[Archive] Error fetching first image for ${feedID}:`, error);
      }
      return false;
    }
  };

  const fetchProjects = async () => {
    if (!customerId) return;

    try {
      const projectsData = await getProjectsByCustomerId(customerId);

      // Filter only archived projects (status === "Terminado")
      const archivedProjects = projectsData.filter((p: any) => p.status === "Terminado");

      setProjects(archivedProjects);

      const initialLoadingStates: Record<string, boolean> = {};
      const newFtps3ImageUrls: Record<string, string> = {};
      const newRetryCounts: Record<string, number> = {};
      const newImageErrors: Record<string, boolean> = {};

      for (const project of archivedProjects) {
        initialLoadingStates[project.id] = false;
        newRetryCounts[project.id] = 0;
        newImageErrors[project.id] = false;

        if (!project?.feedID || project?.feedID === 'null') {
          continue;
        }

        initialLoadingStates[project.id] = true;
        try {
          const data = await getCamaraFirstImage(project.feedID);

          if (data?.url) {
            newFtps3ImageUrls[project.id] = data.url;
          } else {
            newImageErrors[project.id] = true;
            initialLoadingStates[project.id] = false;
          }
        } catch (err: any) {
          // 404: sin firstImage generada todavía (o cámara inexistente). 409: status en
          // Firebase todavía no sincronizado como "Terminado". Casos esperables, no errores reales.
          const status = err?.response?.status;
          if (status !== 404 && status !== 409) {
            console.error(`[Archive] Error fetching first image for ${project.feedID}:`, err);
          }
          newImageErrors[project.id] = true;
          initialLoadingStates[project.id] = false;
        }
      }

      setFtps3ImageUrls(newFtps3ImageUrls);
      setImageRetryCounts(newRetryCounts);
      setImageErrorStates(newImageErrors);

      setImageLoadingStates((prev) => ({
        ...prev,
        ...initialLoadingStates,
      }));

    } catch (error) {
      console.error("[Archive] Error fetching projects:", error);
      toast.error("Error al cargar los proyectos archivados.");
    } finally {
      setIsLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchProjects();
    }
  }, [customerId]);

  useEffect(() => {
    if (!isLoadingProjects) {
      setIsLoadingContent(false);
    } else {
      setIsLoadingContent(true);
    }
  }, [isLoadingProjects]);

  const handleIngresar = (identifier: string) => {
    window.open(`/dashboard/${identifier}`, '_blank');
  };

  const handleImageLoad = (e: any, projectId: string) => {
    const img = e.target;
    const isVertical = img.naturalHeight > img.naturalWidth;
    setImageOrientations(prev => ({
      ...prev,
      [projectId]: isVertical ? 'object-fit-contain' : 'object-fit-cover'
    }));
    setImageLoadingStates(prev => ({
      ...prev,
      [projectId]: false
    }));
    setImageErrorStates(prev => ({
      ...prev,
      [projectId]: false,
    }));
    setImageRetryCounts(prev => ({
      ...prev,
      [projectId]: 0,
    }));
  };

  const handleImageError = async (projectId: string, project: any) => {
    const currentRetries = (imageRetryCounts[projectId] ?? 0) as number;

    if (currentRetries < MAX_IMAGE_RETRIES) {
      setImageRetryCounts(prev => ({
        ...prev,
        [projectId]: currentRetries + 1
      }));

      const success = await fetchSingleFtps3ImageUrl(
          projectId,
          project.feedID
      );

      if (success) {
        return;
      }
    }

    setImageLoadingStates((prev) => ({
      ...prev,
      [projectId]: false,
    }));

    setImageErrorStates((prev) => ({
      ...prev,
      [projectId]: true,
    }));
  };

  return (
    <>
      <div className="w-full">
        <h1 className="mb-6 text-xl font-bold">
          Cámaras Archivadas
        </h1>

        {isLoadingContent ? (
          <div className="flex justify-center items-center h-full min-h-[300px]">
            <Loader size={48} className="animate-spin text-blue-500" />
          </div>
        ) : projects.length === 0 ? (
          <div className="flex justify-center items-center h-full min-h-[300px]">
            <p className="text-gray-500 text-lg">No hay cámaras archivadas</p>
          </div>
        ) : (
          <div className="project-grid">
            {projects.map((project) => (
              <div key={project.id} className="project-card">
                <div className={`project-image-wrapper bg-white`}>
                  {!imageErrorStates[project.id] ? (
                    <ProjectLastFrameImage
                      project={project}
                      ftps3ImageUrls={ftps3ImageUrls}
                      imageLoadingStates={imageLoadingStates}
                      imageErrorStates={imageErrorStates}
                      imageOrientations={imageOrientations}
                      handleImageLoad={handleImageLoad}
                      handleImageError={handleImageError}
                      handleIngresar={handleIngresar}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-50" />
                  )}
                  <div className="project-title-overlay">
                    {project.Name} - {project.identifier}
                  </div>
                </div>
                <div className="project-button-bar">
                  <div className="project-button-left">
                    <button onClick={() => handleIngresar(project.identifier)}>
                      <ArrowRight size={18} />
                    </button>
                  </div>

                  <div className="project-button-right">
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

    </>
  );
}

export default ArchiveCameras;
